import React, { useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import './i18n';
import { Language } from './types';
import { theme } from './styles/theme';
import { menuData } from './data/menuData';
import Header from './components/Header';
import ServiceStatus from './components/ServiceStatus';
import QuickNav from './components/QuickNav';
import PriceInfo from './components/PriceInfo';
import MenuCategory from './components/MenuCategory';
import Footer from './components/Footer';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: ${theme.typography.fontFamily};
    background: ${theme.colors.background};
    color: ${theme.colors.onSurface};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
  }

  html {
    scroll-behavior: smooth;
  }

`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing(3)} ${theme.spacing(2)};
  width: 100%;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(2)} ${theme.spacing(1)};
  }
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(3)};
`;

const BreakfastNote = styled.div`
  background: ${theme.colors.surface};
  padding: ${theme.spacing(3)};
  border-radius: ${theme.shape.borderRadius}px;
  border-left: 4px solid ${theme.colors.accent};
  margin-bottom: ${theme.spacing(4)};
  box-shadow: ${theme.elevation[2]};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(3)};
  }
`;

const NoteText = styled.p`
  margin: 0;
  color: ${theme.colors.onSurface};
  font-size: ${theme.typography.body1.fontSize};
  line-height: ${theme.typography.body1.lineHeight};
  font-weight: ${theme.typography.body1.fontWeight};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.typography.body2.fontSize};
  }
`;

function App() {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('en');
  const { t } = useTranslation();

  return (
    <AppContainer>
      <GlobalStyle />
      <Header 
        currentLanguage={currentLanguage}
        onLanguageChange={setCurrentLanguage}
      />
      <MainContent>
        <ServiceStatus />
        <PriceInfo />
        <QuickNav language={currentLanguage} />
        <BreakfastNote>
          <NoteText>{t('orderBreakfast')}</NoteText>
        </BreakfastNote>
        
        <MenuContainer>
          {menuData.map(category => (
            <MenuCategory
              key={category.id}
              category={category}
              language={currentLanguage}
            />
          ))}
        </MenuContainer>
      </MainContent>
      <Footer />
    </AppContainer>
  );
}

export default App;
